import { z } from "zod";
import { Chain, Protocol } from "./enums/chain";
import { bigNumberTransformer } from "./transformers/big-number-transformer";
import { dateTransformer } from "./transformers/date-transformer";
import { WRAPPED_TOKEN_ADDRESS } from "../lib/constants";

export const SocialDetailsSchema = z.object({
  twitter: z
    .string()
    .nullish()
    .transform((val) => (val === "null" ? null : val)),
  telegram: z
    .string()
    .nullish()
    .transform((val) => (val === "null" ? null : val)),
  discord: z
    .string()
    .nullish()
    .transform((val) => (val === "null" ? null : val)),
  website: z
    .string()
    .nullish()
    .transform((val) => (val === "null" ? null : val)),
});

export const DexscreenerMetaSchema = z.object({
  boosted: z.boolean().optional(),
  boost_total_amount: z.number().optional(),
  last_boosted_at: z.coerce.date().optional(),
  header_url: z.string().optional(),
  open_graph_url: z.string().optional(),
  description: z.string().optional(),
  eti_updated_at: z.coerce.date().optional(),
});

export const TokenInfoSchema = z.object({
  id: z.string(),
  symbol: z.string().default(""),
  image_url: z.string().nullish(),
  chain: z.enum(Chain),
  supply: bigNumberTransformer,
  created_at: dateTransformer.nullish(),
});

export const PumpLivestreamInfoSchema = z.object({
  description: z.string(),
  reply_count: z.coerce.number(),
  thumbnail: z.string(),
  num_participants: z.coerce.number(),
});

export const TokenSchema = z
  .object({
    id: z.string(),
    name: z.string().default(""),
    symbol: z.string().default(""),
    image_url: z.string().nullish(),
    chain: z.enum(Chain),
    created_at: dateTransformer,
    created_by: z.string().nullish(),
    decimals: z.coerce.number().default(0),
    supply: bigNumberTransformer,
    socials: SocialDetailsSchema.optional(),
    freezable: z.boolean().nullish(),
    mintable: z.boolean().nullish(),
    holders: z.coerce.number().nullish(),
    pro_traders: z.coerce.number().nullish(),
    top10_holders_percentage: z.coerce.number().nullish(),
    dev_percentage: z.coerce.number().nullish(),
    insider_percentage: z.coerce.number().nullish(),
    sniper_percentage: z.coerce.number().nullish(),
    bundler_percentage: z.coerce.number().nullish(),
    dev_interacted: z.boolean().default(false),
    dexscreener_meta: DexscreenerMetaSchema.nullish(),
    pump_livestream_info: PumpLivestreamInfoSchema.nullish(),
  })
  .transform((token) => {
    //todo check with sei
    if (token.id === WRAPPED_TOKEN_ADDRESS) {
      return {
        ...token,
        symbol: "SOL",
        name: "Solana",
      };
    }
    return token;
  });

export const DexSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: z.string().nullable(),
  protocols: z.enum(Protocol).array(),
});

export const PoolStatusSchema = z.enum(["creating", "created"]);

// Helper transformer for string to BigNumber

export const PoolSchema = z
  .object({
    id: z.string(),
    tokens: z.object({
      base: TokenSchema,
      quote: TokenSchema,
    }),
    created_at: dateTransformer,
    chain: z.enum(Chain),
    inverted: z.boolean(),
    migrated_at: z
      .string()
      .transform((val) => new Date(val))
      .nullish(),
    is_migrating: z.boolean().nullish(),
    migrated_to_id: z.string().nullish(),
    migrated_from_id: z.string().nullish(),
    // migrated_from: PoolSchema.nullish(),
    // migrated_to: PoolSchema.nullish(),
    protocol: z.enum(Protocol),
  })
  .transform((pool) => {
    if (!pool.inverted) {
      return pool;
    }

    return {
      ...pool,
      tokens: {
        base: pool.tokens.quote,
        quote: pool.tokens.base,
      },
    };
  });
export type TokenInfo = z.infer<typeof TokenInfoSchema>;
export type Token = z.infer<typeof TokenSchema>;
export type Dex = z.infer<typeof DexSchema>;
export type Pool = z.infer<typeof PoolSchema>;
export type SocialDetails = z.infer<typeof SocialDetailsSchema>;
export type DexscreenerMeta = z.infer<typeof DexscreenerMetaSchema>;
export type PumpLivestreamInfo = z.infer<typeof PumpLivestreamInfoSchema>;
